<script>
import { mapActions, mapGetters } from 'vuex'
import { toArray } from '@/support/utils/apiCollection'

export default {
  name: 'SolutionsList',
  components: {
    Action: () => import('@/components/general/Action'),
    Dropdown: () => import('@/components/general/Dropdown'),
    Datatable: () => import('@/components/general/Datatable'),
    FilterList: () => import('@/components/general/FilterList'),
    Pagination: () => import('@/components/general/Pagination'),
    DropdownLink: () => import('@/components/general/DropdownLink'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    ModalConfirm: () => import('@/components/macro/ModalConfirm'),
    Icon: () => import('@/components/general/Icon'),
    Modal: () => import('@/components/general/Modal'),
    Tabs: () => import('@/components/general/Tabs'),
    SelectField: () => import('@/components/general/SelectField'),
    PageLimitSelector: () => import('@/components/general/PageLimitSelector')
  },

  data () {
    return {
      pagination: {
        query: {},
        filter: {},
        order: {
          active: 'desc',
          name: 'asc'
        },

        limit: 12,
        page: 1,
        lastPage: null
      },

      modalRemoveOpen: false,
      modalDeactivateOpen: false,
      solutionId: null,
      solutionsList: [],
      modalSessionsFiltersOpen: false,
      filteredFormLabels: {
        active: [
          {
            text: this.$t('solutions.manage:filtered-form.input.1.option.1'),
            value: 'true'
          },
          {
            text: this.$t('solutions.manage:filtered-form.input.1.option.2'),
            value: 'false'
          }
        ],

        showOnPortal: [
          {
            text: this.$t('global:form.tracks.yes'),
            value: '1'
          },
          {
            text: this.$t('global:form.tracks.no'),
            value: '0'
          }
        ],

        hasCertificate: [
          {
            text: this.$t('solutions.manage:filtered-form.input.3.option.1'),
            value: '1'
          },
          {
            text: this.$t('solutions.manage:filtered-form.input.3.option.2'),
            value: '0'
          }
        ],

        hasSurvey: [
          {
            text: this.$t('solutions.manage:filtered-form.input.4.option.1'),
            value: '1'
          },
          {
            text: this.$t('solutions.manage:filtered-form.input.4.option.2'),
            value: '0'
          }
        ],

        enrollmentClosed: [
          {
            text: this.$t('global:form.tracks.yes'),
            value: '"closed"'
          },
          {
            text: this.$t('global:form.tracks.no'),
            value: '"open"'
          }
        ]
      },

      filteredForm: {
        active: null,
        show_on_portal: null,
        has_certificate: null,
        has_survey: null,
        enrollment_closed: null
      },

      tabLinks: [
        {
          location: { name: 'solutions.index' },
          text: this.$t('solutions.tabs:link.1')
        },
        {
          location: { name: 'solutions.configs' },
          text: this.$t('solutions.tabs:link.2')
        }
      ]
    }
  },

  computed: {
    ...mapGetters([ 'getPagination' ]),

    canWrite () {
      return this.getContextPermission('solution') === 'write'
    },

    isNonProdEnv () {
      return process.env.NODE_ENV !== 'production'
    }
  },

  watch: {
    'pagination.page' () {
      this.getSolutionList()
    }
  },

  created () {
    this.pagination = this.getPagination
    this.filteredForm = this.getPagination.filter

    this.getSolutionList()
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'setPagination',
      'attemptGetSolutionsList',
      'attemptManageSolution',
      'attemptRemoveSolution',
      'attemptToggleActiveSolution',
      'attemptUpdateSolution'
    ]),

    getToggleClass (isEnabled) {
      return isEnabled ? 'dropdown-toggle--on' : 'dropdown-toggle--off'
    },

    prevPage () {
      if (this.pagination.page > 1) {
        this.pagination.page -= 1
      }
    },

    nextPage () {
      this.pagination.page += 1
    },

    firstPage () {
      this.pagination.page = 1
    },

    lastPage () {
      this.pagination.page = this.pagination.lastPage
    },

    createNewSolution () {
      this.attemptManageSolution(null)
      this.$router.push({ name: 'solutions.manage' })
    },

    searchSolution (search) {
      this.pagination.query = {
        name: search,
        only_name: true
      }

      if (this.pagination.page === 1) {
        this.getSolutionList()
      } else {
        this.pagination.page = 1
      }
    },

    edit (solutionId, tab = 1) {
      this.setFetching(true)

      this.attemptManageSolution(solutionId).then(() => {
        this.$router.push({
          name: 'solutions.manage',
          params: {
            id: solutionId,
            tab: tab
          }
        })
      }).finally(() => {
        this.setFetching(false)
      })
    },

    toggleActive (solutionId, toggleFlag) {
      this.setFetching(true)

      this.attemptToggleActiveSolution(solutionId).then(() => {
        this.getSolutionList()

        if (!toggleFlag) {
          this.setFeedback({ message: this.$t('solutions:feedback.toggle.activate.success') })
        } else {
          this.setFeedback({ message: this.$t('solutions:feedback.toggle.deactivate.success') })
        }
      }).catch((e) => {
        if (e.data.error.message === 'toggle_solution_unable_to_create_event') {
          this.setFeedback({ message: this.$t('solutions:feedback.toggle.integration.error', { integration: 'SAS' }) })
        } else if (this.getErrorMessage(e) === 'cant_activate_solution_no_asset_found') {
          this.setFeedback({ message: this.$t('management.solutions:error.asset.not.found') })
        } else if (e.data.error.message === 'solution__invalid_portfolio') {
          this.setFeedback({ message: this.$t('solutions:feedback.portfolio.invalid') })
        } else if (e.data.error.message === 'solution__portfolio_not_registered') {
          this.setFeedback({ message: this.$t('solutions:feedback.portfolio.not.registered') })
        } else {
          this.setFeedback({ message: this.$t('global.error') })
        }
      }).finally(() => {
        this.setFetching(false)
        this.modalDeactivateOpen = false
      })
    },

    openPage (track) {
      this.attemptManageSolution(track.id).then((data) => {
        if (data.exclusiveToThematicPages === true) {
          window.open(`${process.env.VUE_APP_PORTAL_URL}parceiro/${data.thematicPageSolution[0].thematicPage.slug}/curso/${track.slug}`, '_blank')
        } else if (data.partner && data.partner.id) {
          window.open(`${process.env.VUE_APP_PORTAL_URL}conecta-saber/curso/${track.slug}`, '_blank')
        } else {
          window.open(`${process.env.VUE_APP_PORTAL_URL}curso/${data.slug}`, '_blank')
        }
      }).finally(() => {
        this.setFetching(false)
      })
    },

    openExternalLinkPage (track) {
      window.open(`${process.env.VUE_APP_PORTAL_URL}curso/${track.slug}?isExternalLink=true`, '_blank')
    },

    remove () {
      // this.modalRemoveOpen = false
      // this.setFetching(true)
      // this.attemptRemoveSolution(this.solutionId).then(() => {
      //   this.setFeedback({ message: this.$t(`solutions:feedback.remove.success`) })
      //   this.getSolutionList()
      // }).catch(() => {
      //   this.setFeedback({ message: this.$t(`solutions:feedback.remove.error`) })
      // }).finally(() => {
      //   this.setFetching(false)
      // })
    },

    deactivate () {
      this.toggleActive(this.solutionId, true)
    },

    // confirmRemove (idSolution) {
    // this.solutionId = idSolution
    // this.modalRemoveOpen = true
    // },

    confirmDeactivate (idSolution) {
      this.solutionId = idSolution
      this.modalDeactivateOpen = true
    },

    doCopy (idSolution) {
      const copiedText = `${process.env.VUE_APP_PORTAL_URL}matricular/${idSolution}`

      this.$copyText(copiedText).then(() => {
        this.setFeedback({ message: `Link copiado!<br /> ${copiedText}` })
      }, () => {
        this.setFeedback({ message: `Copie o link de matrícula:<br /> ${copiedText}` })
      })
    },

    cancelDeactivate () {
      this.modalDeactivateOpen = false
    },

    cancelRemove () {
      this.modalRemoveOpen = false
    },

    getSASId (integration) {
      if (!integration) return '--'

      return integration.data && integration.data.sasId ? integration.data.sasId : '--'
    },

    getPortfolioId (integration) {
      if (!integration) return '--'

      return integration.data && integration.data.portfolioId ? integration.data.portfolioId : '--'
    },

    getErrorMessage (e) {
      if (e.data && e.data.error && e.data.error.message) {
        return e.data.error.message
      }

      return null
    },

    displayIntegrationError (solution) {
      if (!solution.active && solution.solutionIntegration.length > 0 && solution.solutionIntegration[0].data.lastIntegration) {
        return !solution.solutionIntegration[0].data.lastIntegration.success
      }
    },

    displayPortfolioIntegrationError (solution) {
      if (solution.solutionIntegration.length > 0 && solution.solutionIntegration[0].data.lastPortfolioIntegration) {
        return !solution.solutionIntegration[0].data.lastPortfolioIntegration.success
      }
    },

    tooltipContent (solution) {
      const error = this.$t('solutions.list:integration.error')
      const code = this.$t('global:code') + ': ' + solution.solutionIntegration[0].data.lastIntegration.code
      const msg = this.$t('global:message') + ': ' + solution.solutionIntegration[0].data.lastIntegration.message

      return `
        <p>${error}</p></br>
        <span>${code}</span></br>
        <span>${msg}</span>
      `
    },

    doSearch () {
      return this.getPagination && this.getPagination.query ? this.getPagination.query.name : null
    },

    getSolutionList () {
      this.setFetching(true)
      this.setPagination(this.pagination)

      this.attemptGetSolutionsList(this.pagination).then(pagination => {
        this.pagination.lastPage = pagination.lastPage
        this.solutionsList = pagination.data
      }).finally(() => {
        this.setFetching(false)
      })
    },

    onLimitChange (newLimit) {
      this.pagination.limit = newLimit

      if (this.pagination.page === 1) {
        this.getSolutionList()
      } else {
        this.pagination.page = 1
      }
    },

    filter () {
      Object.keys(this.filteredForm).map(key => {
        this.pagination.filter[key] = this.filteredForm[key] || undefined

        return key
      })

      if (this.pagination.page === 1) {
        this.getSolutionList()
      } else {
        this.pagination.page = 1
      }

      this.closeFilterModal()
      this.getSolutionList()
    },

    openFilterModal () {
      this.modalSessionsFiltersOpen = true
    },

    closeFilterModal () {
      this.modalSessionsFiltersOpen = false
    },

    clearFilters () {
      Object.keys(this.filteredForm).map(key => {
        this.filteredForm[key] = undefined

        return key
      })

      if (this.pagination.page === 1) {
        this.getSolutionList()
      } else {
        this.pagination.page = 1
      }

      this.getSolutionList()
    },

    buildSolutionUpdateData (apiData) {
      const integrationData = apiData.solutionIntegration && apiData.solutionIntegration[0]
        ? {
          vendorId: apiData.solutionIntegration[0].vendorId,
          vendorFinishId: apiData.solutionIntegration[0].data?.vendorFinishId || null,
          vendorKey: apiData.solutionIntegration[0].data?.vendorKey || null
        }
        : { vendorId: null }

      const themes = []

      if (apiData.solutionTheme) {
        toArray(apiData.solutionTheme).forEach(st => themes.push(st.theme.alias))
      }

      const entrepreneurProfile = []

      if (apiData.solutionEntrepreneurProfile) {
        toArray(apiData.solutionEntrepreneurProfile).forEach(sep => entrepreneurProfile.push(sep.entrepreneurProfile.id))
      }

      const learningPaths = []

      if (apiData.learningPathSolution) {
        toArray(apiData.learningPathSolution).forEach(lps => learningPaths.push(lps.learningPath.id))
      }

      const surveyIds = []

      if (apiData.solutionSurvey) {
        toArray(apiData.solutionSurvey).forEach(ss => surveyIds.push(ss.survey.id))
      }

      const thematicPages = []

      if (apiData.thematicPageSolution) {
        toArray(apiData.thematicPageSolution).forEach(tps => thematicPages.push(tps.thematicPage.id))
      }

      const solutionsRecommendations = []

      if (apiData.recommendationType === 'solutions' && apiData.solutionRecommendation) {
        toArray(apiData.solutionRecommendation).forEach(sr => solutionsRecommendations.push(sr.recommendedSolution.id))
      }

      // Extrair dados de assets por tipo de solução
      const solutionTypeAlias = apiData.solutionType?.alias || ''
      const isVideo = solutionTypeAlias === 'video'
      const isWebinar = solutionTypeAlias === 'webinar'
      const isPodcast = solutionTypeAlias === 'podcast'
      const isExternalActivity = solutionTypeAlias === 'external_activity'
      const isEad = solutionTypeAlias === 'curso_ead'
      const isWhatsapp = solutionTypeAlias === 'dicas_whatsapp'
      const isSolutionClose = apiData.sessionType === 'close'

      let videoUrl = null
      let previewVideoUrl = null
      let podcastUrl = null
      let externalLink = null
      let liveChat = null

      if (apiData.solutionAsset && apiData.solutionAsset.length > 0) {
        if (isVideo || isWebinar) {
          const assetVideo = apiData.solutionAsset.find(sa => sa.asset?.assetType?.alias === 'video')

          videoUrl = assetVideo?.asset?.meta?.video_url || null
        }

        if (isPodcast) {
          podcastUrl = apiData.solutionAsset[0]?.asset?.meta?.podcast_url || null
        }

        if (isExternalActivity) {
          externalLink = apiData.solutionAsset[0]?.asset?.meta?.url || null
        }

        if (isEad || isWhatsapp) {
          previewVideoUrl = apiData.solutionAsset[0]?.asset?.meta?.video_url || null
        }

        if (isWebinar && isSolutionClose) {
          liveChat = apiData.solutionAsset.some(sa => sa.asset?.meta?.chat === 'true')
        }
      }

      return {
        allocation: apiData.allocation ? 1 : 0,
        solution_type: apiData.solutionType?.alias || apiData.solutionType,
        integration: apiData.solutionTypeIntegration?.id || apiData.integration,
        solution_integration_data: JSON.stringify(integrationData),
        name: apiData.name,
        slug: apiData.slug,
        description: apiData.description,
        format: apiData.format,
        call_text: apiData.callText,
        audience: apiData.audience,
        content: apiData.content,
        differentials: apiData.differentials,
        productCode: apiData.productCode || '',
        price: apiData.price,
        payment_type: apiData.paymentType || 'free',
        teaser_link: apiData.teaserLink,
        portfolio_id: apiData.portfolioId || null,
        sas_id: apiData.sasId || null,
        sas_unity_id: apiData.sasUnityId || null,
        sas_project_id: apiData.sasProjectId || null,
        sas_action_id: apiData.sasActionId || null,
        rae_sync_session: apiData.raeSyncSession ? 1 : 0,
        rae_sync_enrollment: apiData.raeSyncEnrollment ? 1 : 0,
        rae_exclusive_enrollment: apiData.raeExclusiveEnrollment ? 1 : 0,
        show_on_portal: apiData.showOnPortal ? 1 : 0,
        certificate_id: apiData.certificate?.id || null,
        session_type: apiData.sessionType,
        enrollment_type: apiData.enrollmentType,
        session_due_time: apiData.sessionDueTime,
        session_reenrollment_await_time: apiData.sessionReEnrollmentAwaitTime,
        video: (isVideo || isWebinar) ? videoUrl : null,
        preview_video: (isEad || isWhatsapp) ? previewVideoUrl : null,
        live_chat: (isSolutionClose && isWebinar) ? liveChat : null,
        podcast: isPodcast ? podcastUrl : null,
        external_link: isExternalActivity ? externalLink : null,
        survey_ids: surveyIds,
        survey_required: apiData.surveyRequired || 0,
        survey_required_for_certificate: apiData.surveyRequiredForCertificate ? 1 : 0,
        themes: themes,
        entrepreneur_profile: entrepreneurProfile,
        workload: apiData.workload || '0h',
        tags: apiData.tags || [],
        seo_title: apiData.seoTitle || '',
        seo_description: apiData.seoDescription || '',
        seo_keywords: apiData.seoKeywords || '',
        learning_paths: learningPaths,
        exclusive_to_thematic_pages: apiData.exclusiveToThematicPages ? 1 : 0,
        exclusive_to_company: apiData.exclusiveToCompany ? 1 : 0,
        has_event: apiData.hasEvent ? 1 : 0,
        thematic_pages: thematicPages,
        recommendation_type: apiData.recommendationType || 'themes',
        solutions_recommendations: solutionsRecommendations
      }
    },

    toggleField (solutionId, fieldName, currentValue) {
      this.setFetching(true)

      this.attemptManageSolution(solutionId)
        .then((apiData) => {
          console.log('=== DEBUG toggleField ===')
          console.log('solutionId:', solutionId)
          console.log('fieldName:', fieldName)
          console.log('currentValue:', currentValue)
          console.log('apiData received:', apiData)
          console.log('solutionType:', apiData?.solutionType)
          console.log('solutionAsset:', apiData?.solutionAsset)

          const updateData = this.buildSolutionUpdateData(apiData)
          const newValue = currentValue ? 0 : 1

          updateData[fieldName] = newValue

          console.log('updateData to send:', updateData)
          console.log('=== END DEBUG ===')

          return this.attemptUpdateSolution({
            id: solutionId,
            data: updateData
          })
        })
        .then(() => {
          this.getSolutionList()
          const status = currentValue ? 'desativado' : 'ativado'

          this.setFeedback({ message: `Campo ${status} com sucesso` })
        })
        .catch((error) => {
          console.error('=== ERROR toggleField ===')
          console.error('error:', error)
          console.error('error.data:', error?.data)
          console.error('error.data.errors:', error?.data?.errors)
          console.error('error.data.message:', error?.data?.message)
          console.error('=== END ERROR ===')
          this.setFeedback({ message: this.$t('global.error') })
        })
        .finally(() => {
          this.setFetching(false)
        })
    }
  }
}
</script>

<template>
  <div class="main-content">
    <ContentHeader
      :title="$t('solutions:header.title')"
      :description="$t('solutions:header.description')"
      background="/assets/images/themes/default/png/solutions_banner.png"
    >
      <Tabs
        v-if="canWrite"
        slot="tabs"
        dark
        :links="tabLinks"
      />
    </ContentHeader>
    <FilterList>
      <Action
        v-if="canWrite"
        slot="button"
        primary
        large
        fixed-width
        type="button"
        :text="$t('solutions.list:btn.add')"
        @click="createNewSolution"
      />

      <Action
        v-if="canWrite"
        slot="button"
        primary
        large
        fixed-width
        type="button"
        :text="$t('sessions.list:btn.filter')"
        @click="openFilterModal"
      />

      <PageLimitSelector
        slot="order"
        :value="pagination.limit"
        storage-key="page_limit_solutions"
        @input="onLimitChange"
      />
      <FilterListSearch
        slot="search"
        :hide-error-details="true"
        :do-search="doSearch()"
        @search="searchSolution"
      />
    </FilterList>
    <div
      v-if="solutionsList.length > 0"
      class="center solutions-table"
    >
      <Datatable :items="solutionsList">
        <template slot="thead">
          <tr>
            <th
              class="th"
              width="300"
            >
              {{ $t('solutions.list:datatable.header.1') }}
            </th>

            <th
              class="th"
              width="170"
            >
              {{ $t('solutions.list:datatable.header.2') }}
            </th>

            <th class="th">
              {{ $t('solutions.list:datatable.header.5') }}
            </th>

            <th class="th text-center">
              {{ $t('solutions.list:datatable.header.3') }}
            </th>

            <th class="th text-center">
              {{ $t('solutions.list:datatable.header.6') }}
            </th>

            <th class="th text-center">
              {{ $t('solutions.list:datatable.header.7') }}
            </th>

            <th
              class="th text-center"
              width="80"
            >
              {{ $t('solutions.list:datatable.header.4') }}
            </th>

            <th
              class="th"
              width="130"
            >
              {{ $t('global:datatable.audit.created-at') }}
            </th>

            <th
              class="th"
              width="180"
            >
              {{ $t('global:datatable.audit.updated-at') }}
            </th>

            <th
              class="th"
              width="20"
            />

            <th
              class="th"
              width="40"
            />
          </tr>
        </template>
        <template
          slot="tbody"
          slot-scope="props"
        >
          <tr>
            <td class="td">
              <span class="td-text bolder">{{ props.item.name }}</span>
            </td>

            <td class="td">
              <span class="td-text">{{ props.item.solutionType.name }}</span>
            </td>

            <td class="td">
              <span class="td-text">{{ props.item.solutionTypeIntegration.integration.name.toLowerCase() == 'local' ? '-' : props.item.solutionTypeIntegration.integration.name }}</span>
            </td>

            <td class="td text-center">
              <span class="td-text">{{ getPortfolioId(props.item.solutionIntegration[0]) }} / {{ getSASId(props.item.solutionIntegration[0]) }}</span>
            </td>

            <td class="td text-center">
              <span
                :class="{ 'td-tag active-ribbon': props.item.showOnPortal, 'td-tag inactive-ribbon': !props.item.showOnPortal }"
              >
                {{ Boolean(props.item.showOnPortal) ? $t('global:yes') : $t('global:no') }}</span>
            </td>

            <td class="td text-center">
              <span
                :class="{ 'td-tag active-ribbon': props.item.externalLink, 'td-tag inactive-ribbon': !props.item.externalLink }"
              >
                {{ Boolean(props.item.externalLink) ? $t('global:yes') : $t('global:no') }}</span>
            </td>

            <td class="td text-center">
              <span
                v-if="!!props.item.active"
                class="td-tag active-ribbon"
              >{{ $t('global.status:active') }}</span>
              <span
                v-else
                class="td-tag inactive-ribbon"
              >{{ $t('global.status:inactive') }}</span>
            </td>

            <td class="td">
              <span class="td-text">{{ formatDate(props.item.createdAt) || '—' }}</span>
            </td>

            <td class="td">
              <span class="td-text td-block">{{ formatDate(props.item.userUpdatedAt || props.item.createdAt) || '—' }}</span>
              <span
                v-if="props.item.userUpdatedAt || props.item.createdAt"
                class="td-text td-audit-sub"
              >{{ formatTime(props.item.userUpdatedAt || props.item.createdAt) }}h{{ auditAuthorLabel(props.item) }}</span>
            </td>

            <td class="td text-center">
              <span
                v-if="displayIntegrationError(props.item)"
                v-tooltip="{
                  content: tooltipContent(props.item),
                  placement: 'bottom-center',
                  classes: ['info'],
                  targetClasses: ['it-has-a-tooltip'],
                  offset: 15,
                  trigger: 'click'
                }"
                class="warning"
              >
                <Icon name="alert-circle" />
              </span>
              <VPopover
                v-if="displayPortfolioIntegrationError(props.item)"
              >
                <Icon name="alert-circle" />
                <template slot="popover">
                  <p>{{ $t('solutions.list:integration.error') }}</p>
                  <span>{{ $t('global:code') + ': ' + props.item.solutionIntegration[0].data.lastPortfolioIntegration.code }}</span>
                  <span>{{ $t('global:message') + ': ' + props.item.solutionIntegration[0].data.lastPortfolioIntegration.message }}</span>
                </template>
              </VPopover>
              <span
                v-if="props.item.metadata.assetUnavailable === true"
                v-tooltip="{
                  content: $t('solutions.asset.file:fetch.error.tooltip'),
                  placement: 'bottom-center',
                  classes: ['info'],
                  targetClasses: ['it-has-a-tooltip'],
                  offset: 15,
                  trigger: 'click'
                }"
                class="warning"
              >
                <Icon name="alert-circle" />
              </span>
            </td>

            <!--
            <td class="td td-button td-small text-right">
-->
            <!--
              <action type="button" :text="$t('global:view.more')" flat @click="edit(props.item.id)"></action>
-->
            <!--
            </td>
-->
            <td
              class="td td-actions"
              width="40"
            >
              <Dropdown
                icon="dots-vertical"
                right
              >
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:edit')"
                  @click="edit(props.item.id, 1)"
                />
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:qualify')"
                  @click="edit(props.item.id, 2)"
                />
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:integrations')"
                  @click="edit(props.item.id, 3)"
                />
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:seo')"
                  @click="edit(props.item.id, 4)"
                />
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:links')"
                  @click="edit(props.item.id, 5)"
                />
                <DropdownLink
                  v-if="props.item.showOnPortal"
                  :text="$t('global:show.page')"
                  @click="openPage(props.item)"
                />
                <DropdownLink
                  v-if="props.item.externalLink"
                  :text="$t('global:show.external.link')"
                  @click="openExternalLinkPage(props.item)"
                />
                <DropdownLink
                  v-if="!props.item.active && canWrite"
                  :text="$t('global:activate')"
                  @click="toggleActive(props.item.id, props.item.active)"
                />
                <DropdownLink
                  v-else-if="canWrite"
                  :text="$t('global:deactivate')"
                  @click="confirmDeactivate(props.item.id)"
                />

                <DropdownLink
                  v-show="props.item.sessionType === 'open'"
                  :text="$t('solutions:menu.direct-url')"
                  @click="doCopy(props.item.id)"
                />

                <!--
 DEV/QA Quick Actions
-->
                <template v-if="isNonProdEnv && canWrite">
                  <div class="dropdown-devqa-separator">
                    <span class="dropdown-devqa-title">Ações rápidas (DEV/QA)</span>
                  </div>
                  <div
                    :class="['dropdown-toggle-wrapper', getToggleClass(Boolean(props.item.active))]"
                  >
                    <DropdownLink
                      :text="`Situação: ${Boolean(props.item.active) ? 'Ativo' : 'Inativo'}`"
                      @click="toggleActive(props.item.id, props.item.active)"
                    />
                  </div>
                  <div
                    v-if="typeof props.item.showOnPortal !== 'undefined'"
                    :class="['dropdown-toggle-wrapper', getToggleClass(Boolean(props.item.showOnPortal))]"
                  >
                    <DropdownLink
                      :text="`Exibir no portal: ${Boolean(props.item.showOnPortal) ? 'Sim' : 'Não'}`"
                      @click="toggleField(props.item.id, 'show_on_portal', props.item.showOnPortal)"
                    />
                  </div>
                  <div
                    v-if="typeof props.item.exclusiveToCompany !== 'undefined'"
                    :class="['dropdown-toggle-wrapper', getToggleClass(Boolean(props.item.exclusiveToCompany))]"
                  >
                    <DropdownLink
                      :text="`Exclusivo PJ: ${Boolean(props.item.exclusiveToCompany) ? 'Sim' : 'Não'}`"
                      @click="toggleField(props.item.id, 'exclusive_to_company', props.item.exclusiveToCompany)"
                    />
                  </div>
                </template>

                <!--
                <dropdown-link :text="$t('global:remove')" @click="confirmRemove(props.item.id, props.index)"></dropdown-link>
-->
              </Dropdown>
            </td>
          </tr>
        </template>
      </Datatable>
      <Pagination
        :active-page="pagination.page"
        :page-count="pagination.lastPage"
        @firstPage="firstPage"
        @lastPage="lastPage"
        @nextPage="nextPage"
        @previousPage="prevPage"
      />
    </div>
    <div
      v-else
      class="center solutions-table"
    >
      <p>Nenhuma solução encontrada para o termo pesquisado</p>
    </div>
    <ModalConfirm
      :active="modalRemoveOpen"
      :title="$t('solutions.manage:remove.title')"
      :description="$t('solutions.manage:remove.description')"
      @confirmAction="remove"
      @cancelAction="cancelRemove"
    />
    <ModalConfirm
      :active="modalDeactivateOpen"
      :title="$t('solutions.manage:deactivate.title')"
      :description="$t('solutions.manage:deactivate.description')"
      @confirmAction="deactivate"
      @cancelAction="cancelDeactivate"
    />
    <Modal
      class="modal-sessions-filters"
      :active="modalSessionsFiltersOpen"
      :cancel="true"
      @close="closeFilterModal"
    >
      <div class="modal-form modal-sessions-filters">
        <h2 class="modal-title">
          {{ $t('sessions.list:filter.title') }}
        </h2>
        <span class="modal-subtitle">{{ $t('sessions.list:filter.description') }}</span>
        <form @submit.prevent="filter">
          <SelectField
            v-model="filteredForm.active"
            class="sessions-solutions-field"
            dark
            :label="$t('solutions.manage:filtered-form.input.1')"
            :items="filteredFormLabels.active"
          />

          <SelectField
            v-model="filteredForm.show_on_portal"
            class="sessions-solutions-field"
            dark
            :label="$t('solutions.manage:filtered-form.input.2')"
            :items="filteredFormLabels.showOnPortal"
          />

          <SelectField
            v-model="filteredForm.has_certificate"
            class="sessions-solutions-field"
            dark
            :label="$t('solutions.manage:filtered-form.input.3')"
            :items="filteredFormLabels.hasCertificate"
          />

          <SelectField
            v-model="filteredForm.has_survey"
            class="sessions-solutions-field"
            dark
            :label="$t('solutions.manage:filtered-form.input.4')"
            :items="filteredFormLabels.hasSurvey"
          />

          <SelectField
            v-model="filteredForm.enrollment_closed"
            class="sessions-solutions-field"
            dark
            :label="$t('solutions.manage:filtered-form.input.5')"
            :items="filteredFormLabels.enrollmentClosed"
          />

          <div class="form-buttons">
            <Action
              primary
              large
              dark
              fixed-width
              type="button"
              :text="$t('sessions.list:btn.clear.filter.action')"
              @click="clearFilters"
            />
            <Action
              secondary
              large
              dark
              submit
              fixed-width
              type="button"
              :text="$t('sessions.list:btn.filter.action')"
            />
          </div>
        </form>
      </div>
    </Modal>
  </div>
</template>

<style>
.tooltip {
  color: #005EB8;
  z-index: 9999;
  font-size: 0.85rem;
  margin: 1px solid #005EB8;
}

.tooltip p {
  margin-top: 5px;
}

.tooltip.info .tooltip-inner, .popover-inner div {
  color: #ffffff!important;
  background: linear-gradient(to bottom, #005EB8 0%,#001B5C 400%);
  padding: 24px;
  border-radius: 5px;
  box-shadow: 0 5px 30px rgba(black, .1);
  max-width: 500px;
}

.popover-inner div p {
  padding-bottom: 10px;
}

.popover-inner div span {
  display: block;
}

.tooltip .info .tooltip-arrow {
  border-color: #005EB8;
}

.warning {
  cursor: pointer;
  margin-left: 10px;
}

.form-buttons {
  display: flex;
  justify-content: space-around;
}
</style>
